/**
 * Coverage & liquidity — the supply side of the marketplace, from the nightly rollup (E50 / G5).
 *
 * This screen shipped **honestly deferred** for a long time: no data source existed, so rather
 * than invent numbers or draw a chart from something adjacent and let an operator mistake it for
 * the real read, it said what it would be and what it was waiting on. `supply.*` is that source.
 *
 * **Two questions, and they fail differently.** *Coverage* is how many pandits could take a
 * booking at all; *liquidity* is whether the ones who exist actually answer. A city can look
 * fully staffed and still lose every booking, and only the second half shows it — which is why
 * `waves_exhausted` sits beside the head-count rather than on another screen.
 *
 * ⛔ **The coverage line is a SNAPSHOT and has no history before the day it started.**
 * Dispatchability is current state — an active profile, a live service area, an unexpired badge —
 * and none of that is kept, so the rollup refuses to write it for a past day rather than record
 * today's number under yesterday's date. A gap in a chart is visibly a gap; a wrong number is not,
 * and this is the chart somebody reads before deciding a city is staffed. The screen says so.
 *
 * ⛔ **Acceptance latency is a mean over ACCEPTED offers only.** An expiry's "latency" is the
 * batch TTL — a constant — so averaging expiries in would move the number when nothing about how
 * fast pandits answer had changed.
 */
import { useState } from 'react';
import { Note, StatusChip } from '../components/primitives';
import { DataTable } from '../components/data';
import { Select } from '../components/forms';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { Section } from '../components/kit';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import { useMetrics } from '../lib/api/hooks/analytics';
import { WINDOWS, windowDates } from './Funnel';
import { toSeries } from './MetricExplorer';

/** The keys this screen reads. The server owns the catalogue; this is the subset it charts. */
export const SUPPLY_KEYS = [
  'supply.dispatchable_pandits',
  'supply.offers_sent',
  'supply.offers_accepted',
  'supply.acceptance_latency_seconds',
  'supply.waves_exhausted',
] as const;

/** ⛔ The one key with no history. Named here so the caveat cannot drift from the column. */
export const SNAPSHOT_KEYS: readonly string[] = ['supply.dispatchable_pandits'];

export const SUPPLY_COPY: Readonly<Record<string, { label: string; note: string }>> = {
  'supply.dispatchable_pandits': {
    label: 'Dispatchable pandits',
    note: 'Could take a booking today — active, listed, skilled, badged, serving a city.',
  },
  'supply.offers_sent': { label: 'Offers sent', note: 'Every offer that went out that day.' },
  'supply.offers_accepted': { label: 'Offers accepted', note: 'Of those, the ones taken.' },
  'supply.acceptance_latency_seconds': {
    label: 'Time to accept (s)',
    note: 'Mean seconds from wave sent to accepted — accepted offers only.',
  },
  'supply.waves_exhausted': {
    label: 'Waves that found nobody',
    note: 'Closed with no acceptance. The number that says a healthy head-count is not answering.',
  },
};

/** Acceptance rate as a percentage, or null when nothing was offered — never 0%. */
export function acceptanceRate(sent: number, accepted: number): number | null {
  /* ⛔ A day with no offers has no rate. Rendering 0% would read as "everybody refused", which is
     a supply emergency, when the truth is that nothing was asked of anybody. */
  return sent === 0 ? null : Math.round((accepted / sent) * 100);
}

export function CoverageLiquidity() {
  const { scope } = useConsole();
  /* The LABEL is the state, and the day count is derived. Holding the number instead meant two
     lookups back and forth with `?? fallback` on each — branches nothing could reach, because
     the value always came from `WINDOWS` in the first place. */
  const [window, setWindow] = useState<string>(WINDOWS[1]!.label);
  const days = WINDOWS.find((w) => w.label === window)!.days;
  const dates = windowDates(days);
  const metrics = useMetrics({ date_from: dates.from, date_to: dates.to }, SUPPLY_KEYS);

  const points = metrics.data?.items ?? [];
  const { days: dayLabels, byKey } = toSeries(points);
  /* The most recent day this key actually has, which is not always the most recent day in the
     window — the snapshot key starts later than the dated ones. Read off the key's OWN dates
     rather than by walking the window backwards: a scan needs a "found nothing" fallback that
     `toSeries` can never produce, and an unreachable branch is a line the gate can only be
     argued past. */
  const latest = (key: string): number | null => {
    const row = byKey.get(key);
    /* `toSeries` only creates a row when it adds a point, so a row that exists has a latest
       date and that date has a value. Asserted rather than defended: a `?? null` here would be
       a branch nothing can reach. */
    if (!row) return null;
    return row.get([...row.keys()].sort().at(-1)!)!;
  };

  const sent = latest('supply.offers_sent') ?? 0;
  const accepted = latest('supply.offers_accepted') ?? 0;
  const rate = acceptanceRate(sent, accepted);

  return (
    <>
      <div style={{ display: 'flex', gap: 'var(--sp-md)', alignItems: 'end', flexWrap: 'wrap' }}>
        {/* `WINDOWS` is `{label, days}` — the label is what an operator reads and `days` is what
            the server takes. Mapping the objects straight into `options` rendered
            "[object Object]", and every other test here still passed, because none of them
            touched this control. */}
        <Select
          density="admin"
          label="Window"
          value={window}
          options={WINDOWS.map((w) => w.label)}
          onChange={setWindow}
        />
        <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>
          Read as {scope}. Written nightly by the rollup, never computed here.
        </span>
      </div>

      {metrics.isPending && <LoadingState message="Reading the supply rollup…" />}
      {metrics.isError && (
        <ErrorState
          title="Could not read the supply rollup"
          cause={errorMessage(metrics.error)}
        />
      )}
      {metrics.isSuccess && points.length === 0 && (
        <EmptyState
          title="The rollup has not written a supply figure yet"
          body="These land the first night the job runs after the keys shipped. An empty window is a job that has not run, not a marketplace with no pandits."
        />
      )}

      {metrics.isSuccess && points.length > 0 && (
        <>
          <Section title="Where supply stands" note="The most recent day in the window">
            <div style={{ display: 'flex', gap: 'var(--sp-md)', flexWrap: 'wrap' }}>
              <StatusChip
                tone="info"
                label={`${latest('supply.dispatchable_pandits') ?? 0} dispatchable`}
              />
              {/* ⛔ A day with no offers has no rate. "—" rather than 0%, which would read as
                  "everybody refused" — a supply emergency that did not happen. */}
              <StatusChip
                tone={rate === null ? 'neutral' : rate >= 50 ? 'success' : 'warn'}
                label={rate === null ? 'No offers that day' : `${rate}% accepted`}
              />
              <StatusChip
                tone={(latest('supply.waves_exhausted') ?? 0) > 0 ? 'warn' : 'success'}
                label={`${latest('supply.waves_exhausted') ?? 0} waves found nobody`}
              />
            </div>
            <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>
              A city can look fully staffed and still lose every booking. The head-count is
              coverage; the last two are whether anybody answered.
            </span>
          </Section>

          <Section title="Day by day" note={`${dayLabels.length} days`} pad={false}>
            <DataTable
              density="compact"
              columns={[
                { key: 'day', label: 'Date' },
                ...SUPPLY_KEYS.map((key) => ({
                  key,
                  label: SUPPLY_COPY[key]!.label,
                  align: 'right' as const,
                  mono: true,
                })),
              ]}
              rows={dayLabels.map((day) => ({
                id: day,
                day,
                ...Object.fromEntries(
                  SUPPLY_KEYS.map((key) => {
                    const value = byKey.get(key)?.get(day);
                    /* ⛔ An em dash, not a zero. A missing snapshot means "not recorded that
                       day"; a zero means "nobody was dispatchable". Printing 0 for both is the
                       one mistake this screen cannot afford. */
                    return [key, value === undefined ? '—' : String(value)];
                  }),
                ),
              }))}
            />
          </Section>

          <Note>
            <strong>Dispatchable pandits is a snapshot.</strong> It is written only for the day the
            job runs, because none of what makes a pandit dispatchable — an active profile, a live
            service area, an unexpired badge — keeps history. Days before the keys shipped show
            “—”, which means not recorded, not zero. Time to accept is a mean over accepted offers
            only: an expiry’s latency is the batch timeout, and averaging those in would move the
            number when nothing about how fast pandits answer had changed.
          </Note>
        </>
      )}
    </>
  );
}
